// === predictive search (module) ===
const base = window.Shopify?.routes?.root || '/';
const searchInput = document.getElementById('search-input');

// Early exit if input doesn't exist on this page
if (!searchInput) {
  console.warn('[predictive] #search-input not found; skipping bind.');
} else {
  // --- state ---
  const MIN_LEN = 2;
  let debounceTimer;
  let activeController = null;
  let requestId = 0;
  let mode = 'presearch';

  // --- tiny helpers ---
  function setSearching(on) {
    const offcanvas = document.getElementById('search-offcanvas');
    if (!offcanvas) return;
    offcanvas.classList.toggle('is-searching', !!on);
  }

  function showPresearch() {
    mode = 'presearch';
    hide('#predictive-pages', '#predictive-products', '#predictive-collections', '#predictive-sidebar', '#predictive-fallback', '#predictive-products-fallback');
    show('#presearch-sidebar', '#presearch-carousels', '#presearch-recommendations', '#presearch-carousels-outer');
    setSearching(false);
  }

  function showPredictiveShell() {
    // Don't hide presearch yet; just show loading
    setSearching(true);
  }

  function swapToPredictiveUI() {
    hide('#presearch-carousels', '#presearch-recommendations', '#presearch-sidebar', '#presearch-carousels-outer');
  }

  function $(sel) { return document.querySelector(sel); }
  function hide(...sels){ sels.forEach(s => $(s)?.setAttribute('hidden','true')); }
  function show(...sels){ sels.forEach(s => $(s)?.removeAttribute('hidden')); }

  function toggleVisibility(container, list = []) {
    if (!container) return;
    if (!Array.isArray(list) || list.length === 0) container.setAttribute('hidden','true');
    else container.removeAttribute('hidden');
  }

  function safeText(el, text){
    // Helper to set textContent, not innerHTML
    el.textContent = text ?? '';
    return el;
  }

  // dataset limit with fallback
  const getLimit = () => {
    const isMobile = window.matchMedia("(max-width: 992px)").matches;
    const n = isMobile ? 6 : 5;
    return n;
  };


  // --- rendering ---
  function renderCollections(collections = []) {
    const container = document.getElementById('predictive-collections');
    toggleVisibility(container, collections);
    if (!container) return;

    const { collections: tCollections } = window.__predictiveSearchConfig?.i18n ?? { collections: 'Collections' };

    const wrapper = document.createElement('div');
    wrapper.className = 'search-sidebar';

    const title = document.createElement('div');
    title.className = 'grid-title fw-medium';
    safeText(title, tCollections);

    const ul = document.createElement('ul');
    ul.className = 'predictive-collection-grid search-sidebar-list';

    collections.slice(0, 10).forEach(c => {
      const li = document.createElement('li');
      li.className = 'predictive-item';
      const a = document.createElement('a');
      a.className = 'collection-tile';
      a.href = c?.url || '#';
      safeText(a, c?.title ?? '');
      li.appendChild(a);
      ul.appendChild(li);
    });

    wrapper.append(title, ul);
    container.replaceChildren(wrapper);
  }

  function renderPages(pages = []) {
    const container = document.getElementById('predictive-pages');
    toggleVisibility(container, pages);
    if (!container) return;

    const { pages: tPages } = window.__predictiveSearchConfig?.i18n ?? { pages: 'Pages' };

    const wrapper = document.createElement('div');
    wrapper.className = 'search-sidebar';

    const title = document.createElement('div');
    title.className = 'grid-title fw-medium';
    safeText(title, tPages);

    const ul = document.createElement('ul');
    ul.className = 'predictive-collection-grid search-sidebar-list';

    pages.slice(0, 10).forEach(p => {
      const li = document.createElement('li');
      li.className = 'predictive-item';
      const a = document.createElement('a');
      a.className = 'page-tile';
      a.href = p?.url || '#';
      safeText(a, p?.title ?? '');
      li.appendChild(a);
      ul.appendChild(li);
    });

    wrapper.append(title, ul);
    container.replaceChildren(wrapper);
  }

  function renderProducts(products = []) {
    const container = document.getElementById('predictive-products');
    if (!container || products.length === 0) return;

    const limit = getLimit(container, 5);

    const { products: tProducts, viewAll } = window.__predictiveSearchConfig?.i18n ?? { products: 'Products', viewAll: 'View all' };
    const handles = products.slice(0, limit).map(p => p?.handle).filter(Boolean);

    renderHandlesViaMcShopify(handles).then(html => {
      if (!html) return;

      // Build DOM safely, only inject known-safe section HTML for product cards
      const wrapper = document.createElement('div');

      const title = document.createElement('div');
      title.className = 'grid-title fw-medium';
      const span = document.createElement('span');
      safeText(span, tProducts);
      title.appendChild(span);

      const grid = document.createElement('div');
      grid.className = 'grid';
      grid.setAttribute('data-container', '');
      // Product card HTML from sections is considered trusted theme HTML
      grid.innerHTML = html;

      const viewAllWrap = document.createElement('div');
      viewAllWrap.className = 'search-view-all';
      const a = document.createElement('a');
      a.className = 'view-all';
      a.href = `${base}search?q=${encodeURIComponent(searchInput.value.trim())}`;
      safeText(a, viewAll);
      viewAllWrap.appendChild(a);

      wrapper.append(title, grid, viewAllWrap);
      container.removeAttribute('hidden');
      container.replaceChildren(wrapper);
    });
  }

  async function renderHandlesViaMcShopify(handles = []) {
    if (!window.McShopify || handles.length === 0) return '';

    const fetchSectionHTML = (handle) => new Promise((resolve, reject) => {
      try {
        McShopify.section({
          path: `${Shopify.routes.root}products/${handle}`,
          section_id: 'search-carousel',
          onSuccess: (html) => resolve(html),
          onError: reject
        });
      } catch (e) { reject(e); }
    });

    const results = await Promise.allSettled(handles.map(fetchSectionHTML));
    return results.filter(x => x.status === 'fulfilled').map(x => x.value).join('');
  }

  // --- main listener ---
  searchInput.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    const term = searchInput.value.trim();

    // Below threshold: abort any pending fetch and show presearch, no flicker
    if (term.length < MIN_LEN) {
      if (activeController) { activeController.abort(); activeController = null; }
      showPresearch();
      return;
    }

    // Above threshold: show loading but keep presearch visible until we have data
    showPredictiveShell();

    debounceTimer = setTimeout(async () => {
      const thisReq = ++requestId;
      if (activeController) activeController.abort();
      activeController = new AbortController();

      const q = encodeURIComponent(term);
      const url = `${base}search/suggest.json?q=${q}`
        + `&resources[type]=product,collection,page`
        + `&resources[options][fields]=title,product_type,variants.title,variants.sku,tag`
        + `&resources[limit]=10&resources[limit_scope]=each`;

      let data;
      try {
        const r = await fetch(url, { signal: activeController.signal, credentials: 'same-origin' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        // Some Shopify proxies can return HTML on error; guard parse
        const text = await r.text();
        try { data = JSON.parse(text); } catch { throw new Error('Non-JSON suggest response'); }
      } catch (err) {
        if (err?.name !== 'AbortError') {
          console.error('Predictive search error:', err);
          showPresearch();
        }
        return;
      }

      // Ignore stale results or if user backspaced under threshold
      if (thisReq !== requestId) return;
      const current = searchInput.value.trim();
      if (current.length < MIN_LEN || current !== term) return;

      const results = data?.resources?.results ?? {};
      const products = Array.isArray(results.products) ? results.products : [];
      const pages = Array.isArray(results.pages) ? results.pages : [];
      const collections = Array.isArray(results.collections) ? results.collections : [];

      const hasProducts = products.length > 0;
      const hasPages = pages.length > 0;
      const hasCollections = collections.length > 0;

      if (hasProducts || hasPages || hasCollections) {
        mode = 'predictive';
        swapToPredictiveUI();

        const sidebar = document.getElementById('predictive-sidebar');
        const product_results = document.getElementById('predictive-products');
        const no_product_results = document.getElementById('predictive-products-fallback');

        if (!hasPages && !hasCollections) sidebar?.setAttribute('hidden','true');
        else sidebar?.removeAttribute('hidden');

        if (!hasProducts) {
          product_results?.setAttribute('hidden','true');
          no_product_results?.removeAttribute('hidden');
        }
        else  {
          no_product_results?.setAttribute('hidden','true');
          product_results?.removeAttribute('hidden');
        }  

        // default: hide fallback
        hide('#predictive-fallback');
        
        // render blocks
        renderProducts(products);
        renderCollections(collections);
        renderPages(pages);
      } else {
        mode = 'fallback';
        swapToPredictiveUI();

        hide('#predictive-products', '#predictive-sidebar', '#predictive-products-fallback');
        show('#predictive-fallback');
      }
    }, 200);
  });

  // Optional: abort in-flight fetch when input loses focus (reduces wasted work)
  searchInput.addEventListener('blur', () => {
    if (activeController) { activeController.abort(); activeController = null; }
  });
}
